import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router } from "@angular/router";
import { Observable, of, throwError } from "rxjs";
import { catchError, first, map } from "rxjs/operators";
import { SessionService } from "../session.service";
import { LocalStorageService } from "../storage/local-storage.service";
import { AuthService } from "./auth.service";
import { environment } from "../../environments/environment";
import { PermissionService } from "../permission.service";

@Injectable({
  providedIn: "root",
})
export class NotLoggedInGuard implements CanActivate {
  constructor(
    private AuthService: AuthService,
    private SessionService: SessionService,
    private router: Router,
    private LocalStorageService: LocalStorageService,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    const loggedIn = !!this.SessionService.currentUserValue;
    if (loggedIn) {
      this.router.navigate([""]);
      return false;
    } else {
      const authToken = this.LocalStorageService.authToken;
      if (authToken) {
        return this.AuthService.authenticate(authToken).pipe(
          map(
            () => {
              this.router.navigate([""]);
              return false;
            },
            () => {
              return true;
            },
          ),
          catchError((err) => {
            if (
              (err.status >= 400 && err.status < 500) ||
              err.error?.message === "Not authorized"
            ) {
              this.SessionService.logout();
              this.router.navigate([""]);
            } else {
              this.router.navigate(["error"], { state: { fromAuth: true } });
            }

            return of(true);
          }),
        );
      } else {
        return true;
      }
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class LoggedInGuard implements CanActivate {
  constructor(
    private AuthService: AuthService,
    private SessionService: SessionService,
    private router: Router,
    private LocalStorageService: LocalStorageService,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    const loggedIn = !!this.SessionService.currentUserValue;
    if (loggedIn) {
      return true;
    } else {
      const authToken = this.LocalStorageService.authToken;
      if (authToken) {
        return this.AuthService.authenticate(authToken).pipe(
          map(
            () => {
              return true;
            },
            () => {
              this.router.navigate(["auth", "login"]);

              return false;
            },
          ),
          catchError((err) => {
            console.log(err);
            if (
              (err.status >= 400 && err.status < 500) ||
              err.error?.message === "Not authorized"
            ) {
              this.SessionService.logout();
              this.router.navigate(["auth", "login"]);
            } else {
              this.router.navigate(["error"], { state: { fromAuth: true } });
            }

            return of(false);
          }),
        );
      } else {
        this.router.navigate(["auth", "login"]);
        return false;
      }
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class ResetTokenGuard implements CanActivate {
  constructor(private AuthService: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | Observable<boolean> {
    try {
      return this.AuthService.getUserDataWithResetToken(
        route.params.reset_token,
      ).pipe(
        first(),
        map(
          (res) => {
            // route.data.user = res;
            route.data = { ...route.data, user: res };
            return true;
          },
          () => {
            this.router.navigate(["auth", "forgotPassword"]);
            return false;
          },
        ),
        catchError((err, caught) => {
          this.router.navigate(["auth", "forgotPassword"]);
          throw new Error("Error");
        }),
      );
    } catch (e) {
      this.router.navigate(["auth", "forgotPassword"]);
      return false;
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class ActivateTokenGuard implements CanActivate {
  constructor(private AuthService: AuthService, private router: Router) {}

  canActivate(route: ActivatedRouteSnapshot): boolean | Observable<boolean> {
    try {
      return this.AuthService.getUserDataWithActivateToken(
        route.params.activation_token,
      ).pipe(
        first(),
        map(
          (res) => {
            // route.data.user = res;
            route.data = { ...route.data, user: res };
            return true;
          },
          () => {
            this.router.navigate(["auth", "forgotPassword"]);
            return false;
          },
        ),
        catchError((err, caught) => {
          this.router.navigate(["auth", "forgotPassword"]);
          throw new Error("Error");
        }),
      );
    } catch (e) {
      this.router.navigate(["auth", "forgotPassword"]);
      return false;
    }
  }
}

@Injectable({
  providedIn: "root",
})
export class IsSuperGuard implements CanActivate {
  constructor(private SessionService: SessionService, private router: Router) {}

  canActivate(): boolean | Observable<boolean> {
    if (this.SessionService.isSuper) {
      return true;
    }
    this.router.navigate([""]);
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class AccountAdminGuard implements CanActivate {
  constructor(
    private PermissionService: PermissionService,
    private router: Router,
  ) {}

  canActivate(): boolean | Observable<boolean> {
    if (this.PermissionService.hasRole("account_admin")) {
      return true;
    }
    this.router.navigate([""]);
    return false;
  }
}

@Injectable({
  providedIn: "root",
})
export class NonProdEnvironmentGuard implements CanActivate {
  constructor() {}

  canActivate(): boolean {
    //Only activate if user is not in a production environment
    return !environment.production;
  }
}

@Injectable({
  providedIn: "root",
})
export class FromAuthGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(): boolean | Observable<boolean> {
    if (this.router.getCurrentNavigation()?.extras.state?.fromAuth) {
      return true;
    }
    this.router.navigate([""]);
    return false;
  }
}
